import { investmentToEditForm } from "@/application/mappers/investments/investmentFormMapper";
import { useCallback, useEffect } from "react";
import type { UseInvestmentFormSyncProps } from "@/application/workflows/investments/investmentFormSync.types";
import { useInvestmentFormPreselection } from "@/application/workflows/investments/useInvestmentFormPreselection";
import { useInvestmentFormQuotaSync } from "@/application/workflows/investments/useInvestmentFormQuotaSync";
import { useInvestmentFormSyncEffects } from "@/application/workflows/investments/useInvestmentFormSyncEffects";

export type { UseInvestmentFormSyncProps };

export function useInvestmentFormSync(props: UseInvestmentFormSyncProps) {
  const sync = useInvestmentFormSyncEffects(props);
  const {
    isOpen,
    isEditMode,
    preselectedProject,
    preselectedInvestor,
    preselectedState,
    existingInvestment,
    formData,
    updateFormData,
    setSelectedInvestor,
    selectedQuota,
    setSelectedQuota,
    setFormData,
    loadBeneficiaryDetails,
    hasPrepopulated,
    lastAutoFilledQuotaUuid,
    lastProcessedBeneficiaryDataRef,
  } = sync;

  const computedQuotas = useInvestmentFormQuotaSync({
    isEditMode,
    existingInvestmentQuotaUuid: existingInvestment?.quota?.uuid,
    fiscalQuotas: sync.beneficiaryDetails?.fiscal_quotas,
    formData,
    updateFormData,
    selectedQuota,
    setSelectedQuota,
    lastAutoFilledQuotaUuid,
  });

  useEffect(() => {
    if (isOpen && preselectedProject) loadBeneficiaryDetails(preselectedProject.uuid, true);
  }, [isOpen, preselectedProject, loadBeneficiaryDetails]);

  useEffect(() => {
    if (formData.projectId && !preselectedProject) loadBeneficiaryDetails(formData.projectId, true);
  }, [formData.projectId, preselectedProject, loadBeneficiaryDetails]);

  useInvestmentFormPreselection({
    isOpen,
    isEditMode,
    preselectedProject,
    preselectedInvestor,
    preselectedState,
    formData,
    updateFormData,
    setSelectedInvestor,
    loadBeneficiaryDetails,
    hasPrepopulated,
  });

  useEffect(() => {
    if (isOpen && isEditMode && existingInvestment && !hasPrepopulated.current) {
      setFormData(investmentToEditForm(existingInvestment));
      setSelectedInvestor(existingInvestment.investor);
      if (existingInvestment.beneficiary.uuid)
        loadBeneficiaryDetails(existingInvestment.beneficiary.uuid, false);
      lastAutoFilledQuotaUuid.current = existingInvestment.quota.uuid;
      hasPrepopulated.current = true;
    }
  }, [
    isOpen,
    isEditMode,
    existingInvestment,
    loadBeneficiaryDetails,
    setFormData,
    setSelectedInvestor,
    hasPrepopulated,
    lastAutoFilledQuotaUuid,
  ]);

  const resetSyncState = useCallback(() => {
    hasPrepopulated.current = false;
    lastAutoFilledQuotaUuid.current = null;
    lastProcessedBeneficiaryDataRef.current = null;
  }, [hasPrepopulated, lastAutoFilledQuotaUuid, lastProcessedBeneficiaryDataRef]);

  return {
    loadingBeneficiaryDetails: sync.loadingBeneficiaryDetails,
    beneficiaryDetails: sync.beneficiaryDetails,
    detailError: sync.detailError,
    resetSyncState,
    availableFiscalQuotas: computedQuotas,
  };
}
